'use client';
import { useEffect, useState } from 'react';
import { LuShield, LuSettings } from 'react-icons/lu';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/core/ui/primitives/card';
import { Button } from '@/core/ui/primitives/button';
import { Badge } from '@/core/ui/primitives/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/core/ui/primitives/dialog';
import { PrivilegeAccordion } from './PrivilegeAccordion';
import { updateSecurityProfile } from '../actions';
interface Privilege {
  id: string;
  scope: string;
  feature: string;
  description: string;
}
interface Profile {
  id: string;
  name: string;
  privileges: string[];
}
interface ProfileCardProps {
  profile: Profile;
  groupedPrivileges: Record<string, Privilege[]>;
  enabledFeatures: Record<string, boolean>;
  /** Called after successful mutations to reload data */
  onRefresh?: () => void;
}
export function ProfileCard({ profile, groupedPrivileges, enabledFeatures, onRefresh }: ProfileCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selection, setSelection] = useState<Set<string>>(() => new Set(profile.privileges));
  useEffect(() => {
    if (isOpen) {
      setSelection(new Set(profile.privileges));
    }
  }, [isOpen, profile.privileges]);
  const handleToggle = (privilegeId: string, next: boolean) => {
    setSelection((prev) => {
      const nextSet = new Set(prev);
      if (next) {
        nextSet.add(privilegeId);
      } else {
        nextSet.delete(privilegeId);
      }
      return nextSet;
    });
  };
  return (
    <>
      <Card className='group relative overflow-hidden transition-all hover:shadow-md hover:border-primary/50'>
        <div className='pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100' />

        <CardHeader className='pb-3'>
          <div className='flex items-start justify-between'>
            <div className='space-y-1'>
              <CardTitle className='flex items-center gap-2 text-xl'>
                <LuShield className='h-5 w-5 text-primary' />
                {profile.name}
              </CardTitle>
              <CardDescription>{profile.privileges.length} active privileges</CardDescription>
            </div>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='shrink-0 text-muted-foreground hover:text-primary'
                  type='button'>
                  <LuSettings className='h-5 w-5' />
                </Button>
              </DialogTrigger>
              <DialogContent className='max-w-3xl max-h-[85vh] flex flex-col'>
                <DialogHeader className='pb-4'>
                  <DialogTitle className='flex items-center gap-2 text-2xl'>
                    <LuShield className='h-6 w-6 text-primary' />
                    Edit {profile.name}
                  </DialogTitle>
                  <DialogDescription>Configure access capabilities for this security profile.</DialogDescription>
                </DialogHeader>

                <div className='max-h-[60vh] overflow-y-auto pr-2 -mr-2'>
	                  <form
	                    id={`form-${profile.id}`}
	                    action={async (formData) => {
	                      await updateSecurityProfile(formData);
	                      setIsOpen(false);
	                      onRefresh?.();
	                    }}
	                    className='space-y-8'>
	                    <input type='hidden' name='profileId' value={profile.id} />
	                    {Array.from(selection).map((id) => (
	                      <input key={id} type='hidden' name='privileges' value={id} />
	                    ))}
	
	                    <PrivilegeAccordion
	                      groupedPrivileges={groupedPrivileges}
	                      enabledFeatures={enabledFeatures}
	                      selected={selection}
                      onToggle={handleToggle}
                    />
                  </form>
                </div>

                <DialogFooter className='pt-6 mt-auto border-t'>
                  <Button variant='outline' onClick={() => setIsOpen(false)}>
                    Cancel
                  </Button>
                  <Button type='submit' form={`form-${profile.id}`}>
                    Save changes
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>

        <CardContent>
          <div className='flex flex-wrap gap-1.5'>
            {profile.privileges.slice(0, 5).map((p) => (
              <Badge key={p} variant='secondary' className='font-mono text-[10px] px-1.5'>
                {p}
              </Badge>
            ))}
            {profile.privileges.length > 5 && (
              <Badge variant='outline' className='text-[10px] px-1.5 text-muted-foreground'>
                +{profile.privileges.length - 5} more
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
}
